import { Link } from "@tanstack/react-router";
import { CalendarX, CreditCard, RotateCcw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card";

/**
 * The standing statement of the cancellation and refund policy.
 *
 * One page the booking flow, the cancel dialogs and the cancellation emails all
 * point at, so the three of them cannot drift into telling patients three
 * slightly different things.
 *
 * The rule itself is enforced by the API (`CANCELLATION_NOTICE_HOURS` in
 * `appointment.helper.ts`) and every screen that has an appointment in hand
 * reads the live numbers from `cancellationPolicy` on the response. This page
 * has no appointment, so it states the policy in general terms.
 */
export function CancellationPolicyPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-3xl py-12 md:py-16">
        <h1 className="headline mb-4 text-3xl text-foreground md:text-4xl">
          Cancellation and refund policy
        </h1>
        <p className="body-copy mb-10 text-lg text-muted-foreground">
          What happens to the appointment fee when a booking is cancelled.
        </p>

        <div className="space-y-6">
          <Card>
            <CardContent className="flex gap-4 p-6">
              <CreditCard
                className="mt-1 h-5 w-5 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <div>
                <h2 className="mb-2 text-lg font-semibold text-foreground">
                  When you are charged
                </h2>
                <p className="text-muted-foreground">
                  Nothing is charged when you submit a request. The $50
                  appointment fee is charged to your saved card only once a
                  provider confirms your appointment, and it holds that time slot
                  exclusively for you. Any remaining balance for the visit is
                  charged after it takes place.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex gap-4 p-6">
              <CalendarX
                className="mt-1 h-5 w-5 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <div>
                <h2 className="mb-2 text-lg font-semibold text-foreground">
                  Cancelling
                </h2>
                <p className="mb-3 text-muted-foreground">
                  Cancelling at least <strong>24 hours</strong> before your
                  appointment is free, and the $50 is refunded in full.
                </p>
                <p className="text-muted-foreground">
                  Cancelling with less notice than that, or not attending, means
                  the $50 is kept, because your provider's time is reserved for
                  you and can no longer be offered to another patient who needs
                  it. Nothing further is charged in either case.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex gap-4 p-6">
              <RotateCcw
                className="mt-1 h-5 w-5 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <div>
                <h2 className="mb-2 text-lg font-semibold text-foreground">
                  Refunds
                </h2>
                <p className="text-muted-foreground">
                  Refunds go back to the card you paid with and normally reach
                  your account within 5 to 10 business days. You are never
                  charged a fee for an appointment that Apptly Health or your provider
                  cancels, whenever that happens.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-10 overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <caption className="sr-only">
              What happens to the $50 appointment fee in each case
            </caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="py-3 pr-4 font-semibold">
                  Who cancels
                </th>
                <th scope="col" className="py-3 pr-4 font-semibold">
                  When
                </th>
                <th scope="col" className="py-3 font-semibold">
                  The $50 fee
                </th>
              </tr>
            </thead>
            <tbody className="text-muted-foreground">
              <tr className="border-b border-border">
                <td className="py-3 pr-4">You</td>
                <td className="py-3 pr-4">24 hours or more before</td>
                <td className="py-3">Refunded in full</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-3 pr-4">You</td>
                <td className="py-3 pr-4">Less than 24 hours before</td>
                <td className="py-3">Kept as the late cancellation fee</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-3 pr-4">You</td>
                <td className="py-3 pr-4">You do not attend</td>
                <td className="py-3">Kept as the no-show fee</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-3 pr-4">Apptly Health or your provider</td>
                <td className="py-3 pr-4">Any time</td>
                <td className="py-3">Refunded in full</td>
              </tr>
              <tr>
                <td className="py-3 pr-4">
                  Apptly Health moves you to another provider
                </td>
                <td className="py-3 pr-4">Any time</td>
                <td className="py-3">
                  Refunded in full, then charged again when the new appointment
                  is confirmed
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-sm text-muted-foreground">
          If no fee was ever charged, there is nothing to refund and nothing to
          keep.
        </p>

        <p className="mt-10 text-muted-foreground">
          Questions about a specific appointment? Email{" "}
          <a
            className="text-primary underline"
            href="mailto:contact@apptlyhealth.com"
          >
            contact@apptlyhealth.com
          </a>{" "}
          or open it from{" "}
          <Link className="text-primary underline" to="/client-area/appointments">
            your appointments
          </Link>
          , where the exact amount is shown before you confirm.
        </p>
      </div>
    </div>
  );
}
